import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../utils/api';
import { PresthygCard, PresthygPageHeader, PresthygButton, PresthygBadge } from '../components/presthyg';
import {
  Sparkles, ChevronRight, CheckCircle, AlertTriangle, Plus, X, Trash2, Save, Eye, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';

const BACKEND = BACKEND_BASE || '';

// --- Helpers ---------------------------------------------------------------
const confidenceTone = (conf, matched) => {
  if (matched && conf >= 0.95) return { bg: '#ECFDF5', fg: '#059669', border: '#A7F3D0', label: `${Math.round(conf * 100)}%` };
  if (matched && conf >= 0.75) return { bg: '#FFFBEB', fg: '#B45309', border: '#FCD34D', label: `${Math.round(conf * 100)}%` };
  return { bg: '#FEF2F2', fg: '#B91C1C', border: '#FCA5A5', label: 'Non trouve' };
};

const ConfidenceBadge = ({ conf, matched }) => {
  const t = confidenceTone(conf, matched);
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border"
      style={{ backgroundColor: t.bg, color: t.fg, borderColor: t.border }}
      title={matched ? `Confiance: ${t.label}` : 'Aucune correspondance'}
    >
      {matched ? <CheckCircle size={10} /> : <AlertTriangle size={10} />}
      {t.label}
    </span>
  );
};

const IconThumb = ({ iconId, size = 28, alt = '' }) => {
  if (!iconId) {
    return (
      <div
        className="rounded flex items-center justify-center border-dashed border text-[9px]"
        style={{ width: size, height: size, borderColor: '#FCA5A5', color: '#B91C1C', backgroundColor: '#FEF2F2' }}
        title="Pas d'icone"
      >
        ?
      </div>
    );
  }
  return (
    <img
      src={`${BACKEND}/api/files/${iconId}`}
      alt={alt}
      style={{ width: size, height: size, objectFit: 'contain' }}
      className="rounded"
    />
  );
};

// --- Main component -------------------------------------------------------
const PlanAI = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [clientName, setClientName] = useState('');
  const [userText, setUserText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftZones, setDraftZones] = useState([]);
  const [planName, setPlanName] = useState('');
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [aiNotes, setAiNotes] = useState('');
  const [refData, setRefData] = useState({
    products_available: [], zones_available: [], frequencies_available: [],
    methods_available: [], icons_available: [], action_icons_available: [],
  });

  useEffect(() => {
    api.get('/clients').then(r => setClients(r.data)).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    if (!userText.trim()) { toast.error('Veuillez decrire le plan de nettoyage'); return; }
    if (!selectedClient) { toast.error('Veuillez selectionner un client'); return; }
    const client = clients.find(c => c.id === selectedClient);
    setClientName(client?.name || '');
    setPlanName(`Plan HACCP ${client?.name || ''}`);
    setGenerating(true);
    try {
      const { data } = await api.post('/ai/generate-plan', { text: userText });
      setDraftZones(data.zones || []);
      setStats(data.stats || null);
      setAiNotes(data.ai_notes || '');
      setRefData({
        products_available: data.products_available || [],
        zones_available: data.zones_available || [],
        frequencies_available: data.frequencies_available || [],
        methods_available: data.methods_available || [],
        icons_available: data.icons_available || [],
        action_icons_available: data.action_icons_available || [],
      });
      setStep(2);
      const total = data.stats?.surfaces_total || 0;
      const matched = data.stats?.surfaces_matched || 0;
      toast.success(`${data.zones?.length || 0} zone(s), ${matched}/${total} surfaces reconnues`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la generation IA');
    } finally {
      setGenerating(false);
    }
  };

  // ---- Update helpers (all deep-clone-safe) -----------------------------
  const mutate = (updater) => {
    setDraftZones(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      updater(copy);
      return copy;
    });
  };

  // Recompute method preview locally from product_types
  const PHASE_CONFIG = {
    degraissant: { phase: 'Nettoyage', steps: ['Preparer la solution', 'Appliquer', 'Frotter', 'Rincer'] },
    detergent: { phase: 'Nettoyage', steps: ['Preparer la solution', 'Appliquer', 'Frotter', 'Rincer'] },
    desinfectant: { phase: 'Desinfection', steps: ['Appliquer', 'Laisser agir', 'Rincer', 'Essuyer'] },
    detartrant: { phase: 'Detartrage', steps: ['Appliquer', 'Laisser agir', 'Frotter', 'Rincer abondamment'] },
    multi_usage: { phase: 'Nettoyage', steps: ['Appliquer', 'Frotter', 'Essuyer'] },
  };
  const PHASE_ORDER_LOCAL = { Nettoyage: 1, Detartrage: 2, Desinfection: 3 };
  const normAccent = (s) => (s || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const iconByStep = (stepName) => {
    const icons = refData.action_icons_available || [];
    const target = normAccent(stepName);
    if (!target) return null;
    // Exact match
    const exact = icons.find(i => normAccent(i.name) === target);
    if (exact) return exact.id;
    // Containment match (step contains icon name or vice versa)
    const contain = icons.find(i => {
      const n = normAccent(i.name);
      return n && (n.includes(target) || target.includes(n));
    });
    return contain ? contain.id : null;
  };
  const recomputeMethodSteps = (surface) => {
    const types = (surface.products || []).map(p => p.product_type).filter(Boolean);
    const phasesMap = {};
    types.forEach(t => {
      const cfg = PHASE_CONFIG[t];
      if (!cfg) return;
      if (!phasesMap[cfg.phase]) phasesMap[cfg.phase] = [];
      cfg.steps.forEach(stepName => {
        if (!phasesMap[cfg.phase].some(s => s.name === stepName)) {
          phasesMap[cfg.phase].push({ name: stepName, icon_id: iconByStep(stepName) });
        }
      });
    });
    return Object.keys(phasesMap)
      .sort((a, b) => (PHASE_ORDER_LOCAL[a] || 99) - (PHASE_ORDER_LOCAL[b] || 99))
      .map(phase => ({ phase, steps: phasesMap[phase] }));
  };

  const updateZoneId = (zIdx, newZoneId) => mutate(c => {
    const zone = refData.zones_available.find(z => z.id === newZoneId);
    c[zIdx].zone_id = newZoneId || null;
    c[zIdx].zone_name = zone?.name || c[zIdx].zone_name;
    c[zIdx].matched = !!newZoneId;
    c[zIdx].confidence = newZoneId ? 1.0 : 0;
    c[zIdx].warnings = [];
    c[zIdx].user_confirmed = !!newZoneId;
  });

  const createZoneInline = async (zIdx) => {
    const ai = draftZones[zIdx]?.ai_zone_name || draftZones[zIdx]?.zone_name || '';
    const proposed = window.prompt('Nom de la nouvelle zone :', ai);
    if (!proposed || !proposed.trim()) return;
    try {
      const { data } = await api.post('/zones', { name: proposed.trim() });
      const newZone = { id: data.id, name: data.name };
      setRefData(prev => ({ ...prev, zones_available: [...prev.zones_available, newZone] }));
      mutate(c => {
        c[zIdx].zone_id = newZone.id;
        c[zIdx].zone_name = newZone.name;
        c[zIdx].matched = true;
        c[zIdx].confidence = 1.0;
        c[zIdx].warnings = [];
        c[zIdx].user_confirmed = true;
      });
      toast.success(`Zone "${newZone.name}" creee`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur creation zone');
    }
  };

  const updateZoneName = (zIdx, name) => mutate(c => { c[zIdx].zone_name = name; });

  const updateSurfaceField = (zIdx, sIdx, field, value) => mutate(c => { c[zIdx].surfaces[sIdx][field] = value; });

  const updateSurfaceIcon = (zIdx, sIdx, iconId) => mutate(c => {
    const ic = refData.icons_available.find(i => i.id === iconId);
    c[zIdx].surfaces[sIdx].icon_id = iconId || null;
    c[zIdx].surfaces[sIdx].icon_name = ic?.name || '';
    c[zIdx].surfaces[sIdx].warnings = (c[zIdx].surfaces[sIdx].warnings || []).filter(w => !w.toLowerCase().includes('icone'));
  });

  const updateProductId = (zIdx, sIdx, pIdx, productId) => mutate(c => {
    const match = refData.products_available.find(p => p.id === productId);
    const p = c[zIdx].surfaces[sIdx].products[pIdx];
    p.product_id = productId || null;
    p.product_name = match?.name || p.product_name;
    p.product_type = match?.product_type || null;
    p.matched = !!productId;
    p.confidence = productId ? 1.0 : 0;
    p.warnings = [];
    // Recompute method preview + clean surface warnings
    c[zIdx].surfaces[sIdx].method_steps = recomputeMethodSteps(c[zIdx].surfaces[sIdx]);
    c[zIdx].surfaces[sIdx].warnings = (c[zIdx].surfaces[sIdx].warnings || []).filter(w => !w.toLowerCase().includes('produit'));
  });

  const removeProduct = (zIdx, sIdx, pIdx) => mutate(c => {
    c[zIdx].surfaces[sIdx].products.splice(pIdx, 1);
    c[zIdx].surfaces[sIdx].method_steps = recomputeMethodSteps(c[zIdx].surfaces[sIdx]);
  });

  const addProduct = (zIdx, sIdx) => mutate(c => {
    c[zIdx].surfaces[sIdx].products.push({ product_name: '', product_id: null, matched: false, confidence: 0, warnings: [] });
  });

  const removeSurface = (zIdx, sIdx) => mutate(c => { c[zIdx].surfaces.splice(sIdx, 1); });

  const addSurface = (zIdx) => mutate(c => {
    c[zIdx].surfaces.push({
      surface_name: '', surface_id: null, icon_id: null, icon_name: '', matched: false, confidence: 0,
      products: [{ product_name: '', product_id: null, matched: false, confidence: 0, warnings: [] }],
      method_id: null, method_name: '', method_steps: [],
      frequency_id: refData.frequencies_available[0]?.id || null,
      frequency_name: refData.frequencies_available[0]?.name || '',
      control: 'Visuel', responsible: 'Equipe', warnings: ['Nouvelle surface a completer'],
    });
  });

  const removeZone = (zIdx) => setDraftZones(prev => prev.filter((_, i) => i !== zIdx));

  const addZone = () => setDraftZones(prev => [...prev, {
    zone_name: '', zone_id: null, matched: false, confidence: 0, warnings: [],
    surfaces: [],
  }]);

  // ---- Save -------------------------------------------------------------
  const handleSave = async () => {
    if (!planName.trim()) { toast.error('Nom du plan requis'); return; }

    // MANDATORY: every zone must be explicitly validated (confirmed by user OR matched with high confidence).
    const pendingZones = draftZones.filter(z => {
      const isConfirmed = z.user_confirmed || (z.matched && z.confidence >= 0.95);
      return !isConfirmed;
    });
    if (pendingZones.length > 0) {
      const names = pendingZones.map(z => `"${z.ai_zone_name || z.zone_name}"`).join(', ');
      toast.error(`Validation requise: ${pendingZones.length} zone(s) non confirmee(s): ${names}. Selectionnez la zone correspondante dans la base ou creez une nouvelle zone.`);
      return;
    }

    const validZones = draftZones.filter(z => z.zone_id && z.surfaces.some(s => s.products.some(p => p.product_id)));
    if (validZones.length === 0) { toast.error('Au moins une zone avec un produit assigne est requise'); return; }

    // Warn if unresolved items (non-zone)
    let unresolved = 0;
    draftZones.forEach(z => {
      z.surfaces.forEach(s => {
        if (!s.icon_id) unresolved++;
        s.products.forEach(p => { if (!p.product_id) unresolved++; });
      });
    });
    if (unresolved > 0) {
      const ok = window.confirm(`${unresolved} element(s) non resolus seront ignores a la sauvegarde. Continuer ?`);
      if (!ok) return;
    }

    setSaving(true);
    try {
      const { data } = await api.post('/ai/save-plan', {
        client_id: selectedClient,
        client_name: clientName,
        plan_name: planName,
        zones: validZones,
      });
      setResult(data);
      setStep(3);
      toast.success('Plan sauvegarde !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // ---- Render -----------------------------------------------------------
  const pendingZoneCount = draftZones.filter(z => !(z.user_confirmed || (z.matched && z.confidence >= 0.95))).length;
  const totalWarnings = draftZones.reduce(
    (acc, z) => acc + (z.warnings?.length || 0) + z.surfaces.reduce(
      (a, s) => a + (s.warnings?.length || 0) + s.products.reduce((b, p) => b + (p.warnings?.length || 0), 0), 0,
    ), 0,
  );


  return (
    <Layout>
      <div data-testid="plan-ai-page" className="max-w-7xl mx-auto">
        <PresthygPageHeader title="Creer un plan avec IA" subtitle="Decrivez votre plan en langage naturel, verifiez, corrigez, puis sauvegardez" />

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {[{ n: 1, label: 'Description' }, { n: 2, label: 'Validation' }, { n: 3, label: 'Resultat' }].map((s, i) => (
            <React.Fragment key={s.n}>
              {i > 0 && <ChevronRight size={16} style={{ color: 'var(--ph-slate200)' }} />}
              <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium" style={{
                borderRadius: 'var(--ph-radius-pill)',
                backgroundColor: step >= s.n ? 'var(--ph-navy)' : 'var(--ph-slate100)',
                color: step >= s.n ? '#fff' : 'var(--ph-slate500)',
              }}>
                <span>{s.n}</span><span>{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* STEP 1: Text input */}
        {step === 1 && (
          <div data-testid="step-text-input">
            <PresthygCard className="p-6 mb-6">
              <div className="grid grid-cols-2 gap-6 mb-5">
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--ph-navy)' }}>Client</label>
                  <select value={selectedClient} onChange={e => setSelectedClient(e.target.value)}
                    data-testid="ai-client-select"
                    className="w-full px-4 py-2.5 border text-sm"
                    style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }}>
                    <option value="">-- Choisir un client --</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div />
              </div>

              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--ph-navy)' }}>
                Description du plan de nettoyage
              </label>
              <textarea
                value={userText}
                onChange={e => setUserText(e.target.value)}
                data-testid="ai-text-input"
                rows={8}
                placeholder={"Exemple:\nPreparation froide avec trancheuse, evier, plan de travail nettoyes avec Prest'hyg food alcalinobact.\nPreparation chaude avec four, plancha, friteuse, plan de travail et sol nettoyes avec Prest'hyg food alcalinobact sauf le sol nettoye avec Prest'hyg food 180."}
                className="w-full px-4 py-3 border text-sm leading-relaxed"
                style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)', fontFamily: 'var(--font-body)', resize: 'vertical' }}
              />
              <p className="text-xs mt-2" style={{ color: 'var(--ph-slate500)' }}>
                Decrivez les zones, surfaces et produits utilises. L'IA generera un brouillon que vous pourrez corriger avant de sauvegarder.
              </p>
            </PresthygCard>

            <div className="flex justify-end">
              <PresthygButton variant="teal" onClick={handleGenerate} disabled={generating} data-testid="generate-ai-btn">
                {generating ? (
                  <><span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Generation en cours...</>
                ) : (
                  <><Sparkles size={16} /> Generer avec IA</>
                )}
              </PresthygButton>
            </div>
          </div>
        )}

        {/* STEP 2: Validation + Edit */}
        {step === 2 && (
          <div data-testid="step-edit">
            {/* Summary bar */}
            {stats && (
              <PresthygCard className="p-5 mb-5" data-testid="ai-stats-bar">
                <div className="flex items-center justify-between gap-6 flex-wrap">
                  <div className="flex items-center gap-6 flex-wrap">
                    <div>
                      <div className="text-[11px] uppercase font-semibold tracking-wide" style={{ color: 'var(--ph-slate500)' }}>Zones</div>
                      <div className="text-2xl font-bold" style={{ color: 'var(--ph-navy)', fontFamily: 'var(--font-display)' }}>{stats.zones}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase font-semibold tracking-wide" style={{ color: 'var(--ph-slate500)' }}>Surfaces reconnues</div>
                      <div className="text-2xl font-bold" style={{ color: stats.surfaces_matched === stats.surfaces_total ? '#059669' : '#B45309', fontFamily: 'var(--font-display)' }}>
                        {stats.surfaces_matched}<span className="text-base opacity-60">/{stats.surfaces_total}</span>
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase font-semibold tracking-wide" style={{ color: 'var(--ph-slate500)' }}>Produits reconnus</div>
                      <div className="text-2xl font-bold" style={{ color: stats.products_matched === stats.products_total ? '#059669' : '#B45309', fontFamily: 'var(--font-display)' }}>
                        {stats.products_matched}<span className="text-base opacity-60">/{stats.products_total}</span>
                      </div>
                    </div>
                    {totalWarnings > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-full" style={{ backgroundColor: '#FFFBEB', border: '1px solid #FCD34D' }}>
                        <AlertTriangle size={14} style={{ color: '#B45309' }} />
                        <span className="text-xs font-semibold" style={{ color: '#B45309' }}>{totalWarnings} avertissement(s) a verifier</span>
                      </div>
                    )}
                  </div>
                </div>
                {aiNotes && (
                  <div className="mt-3 flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                    <Info size={14} style={{ color: '#1D4ED8', marginTop: 2 }} />
                    <div className="text-xs" style={{ color: '#1E3A8A' }}><strong>Notes IA:</strong> {aiNotes}</div>
                  </div>
                )}
              </PresthygCard>
            )}

            {/* Plan name */}
            <PresthygCard className="p-5 mb-5">
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--ph-navy)' }}>Nom du plan</label>
              <input type="text" value={planName} onChange={e => setPlanName(e.target.value)}
                data-testid="ai-plan-name"
                className="w-full max-w-md px-4 py-2.5 border text-sm"
                style={{ borderColor: 'var(--ph-slate200)', borderRadius: 'var(--ph-radius-md)' }} />
            </PresthygCard>

            {/* Zones */}
            {draftZones.map((zone, zIdx) => {
              // Build ordered options: suggestions first, then the rest
              const suggestionIds = new Set((zone.suggestions || []).map(s => s.id));
              const orderedZones = [
                ...(zone.suggestions || []).map(s => ({ ...s, is_suggestion: true })),
                ...refData.zones_available.filter(z => !suggestionIds.has(z.id)).map(z => ({ ...z, is_suggestion: false })),
              ];
              const aiName = zone.ai_zone_name || zone.zone_name;
              const isConfirmed = zone.user_confirmed || (zone.matched && zone.confidence >= 0.95);
              return (
              <PresthygCard key={zIdx} className="mb-5 overflow-hidden" data-testid={`zone-block-${zIdx}`}>
                {/* Zone header */}
                <div className="px-5 py-4" style={{ backgroundColor: 'var(--ph-navy)' }}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex flex-col gap-2 flex-1 min-w-0">
                      {/* Row 1: AI-detected original text */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          Detecte par IA
                        </span>
                        <span className="text-sm font-medium text-white/90" data-testid={`ai-zone-name-${zIdx}`}>
                          "{aiName}"
                        </span>
                        <ConfidenceBadge conf={zone.confidence} matched={zone.matched} />
                      </div>
                      {/* Row 2: manual zone selection */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          Zone base de donnees
                        </span>
                        <select
                          value={zone.zone_id || ''}
                          onChange={e => updateZoneId(zIdx, e.target.value)}
                          data-testid={`zone-select-${zIdx}`}
                          className="px-3 py-1.5 text-sm border rounded font-semibold"
                          style={{ borderColor: isConfirmed ? '#6FCF97' : '#FCD34D', backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', minWidth: 260 }}
                        >
                          <option value="" style={{ color: '#000' }}>-- Choisir une zone --</option>
                          {orderedZones.length > 0 && (zone.suggestions || []).length > 0 && (
                            <optgroup label="Suggestions" style={{ color: '#000' }}>
                              {(zone.suggestions || []).map(s => (
                                <option key={s.id} value={s.id} style={{ color: '#000' }}>
                                  {s.name} ({Math.round((s.score || 0) * 100)}%)
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="Toutes les zones" style={{ color: '#000' }}>
                            {refData.zones_available.filter(z => !suggestionIds.has(z.id)).map(z => (
                              <option key={z.id} value={z.id} style={{ color: '#000' }}>{z.name}</option>
                            ))}
                          </optgroup>
                        </select>
                        <button
                          type="button"
                          onClick={() => createZoneInline(zIdx)}
                          data-testid={`create-zone-${zIdx}`}
                          className="px-3 py-1.5 text-xs rounded font-semibold flex items-center gap-1"
                          style={{ backgroundColor: 'var(--ph-teal)', color: '#fff' }}
                          title="Creer une nouvelle zone avec le nom detecte"
                        >
                          <Plus size={12} /> Nouvelle zone
                        </button>
                        <PresthygBadge status={zone.matched ? 'compliant' : 'incomplete'}>
                          {zone.surfaces?.length || 0} surface(s)
                        </PresthygBadge>
                        {!isConfirmed && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"
                            style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                            <AlertTriangle size={11} /> A valider
                          </span>
                        )}
                        {(zone.warnings || []).map((w, i) => (
                          <span key={i} className="text-[11px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>{w}</span>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => removeZone(zIdx)} className="p-1.5 rounded hover:bg-white/10" title="Supprimer la zone" data-testid={`remove-zone-${zIdx}`}>
                      <Trash2 size={16} style={{ color: '#fff' }} />
                    </button>
                  </div>
                </div>

                {/* Surfaces table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ minWidth: '1100px' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--ph-slate50)' }}>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase" style={{ color: 'var(--ph-slate500)', width: '16%' }}>Surface</th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase" style={{ color: 'var(--ph-slate500)', width: '10%' }}>Icone</th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase" style={{ color: 'var(--ph-slate500)', width: '22%' }}>Produit(s)</th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase" style={{ color: 'var(--ph-slate500)', width: '18%' }}>Methode (apercu)</th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase" style={{ color: 'var(--ph-slate500)', width: '12%' }}>Frequence</th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase" style={{ color: 'var(--ph-slate500)', width: '9%' }}>Controle</th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase" style={{ color: 'var(--ph-slate500)', width: '9%' }}>Responsable</th>
                        <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase" style={{ color: 'var(--ph-slate500)', width: '4%' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {zone.surfaces?.map((surface, sIdx) => (
                        <tr key={sIdx} className="border-t align-top" style={{ borderColor: 'var(--ph-slate200)' }} data-testid={`surface-row-${zIdx}-${sIdx}`}>
                          {/* Surface name + warnings */}
                          <td className="px-3 py-3">
                            <div className="flex items-start gap-1.5 mb-1">
                              <ConfidenceBadge conf={surface.confidence} matched={surface.matched} />
                            </div>
                            <input type="text" value={surface.surface_name || ''}
                              onChange={e => updateSurfaceField(zIdx, sIdx, 'surface_name', e.target.value)}
                              className="w-full px-2 py-1.5 border text-sm font-medium"
                              style={{ borderColor: 'var(--ph-slate200)', borderRadius: '8px' }} />
                            {(surface.warnings || []).map((w, i) => (
                              <div key={i} className="mt-1 flex items-start gap-1 text-[10px]" style={{ color: '#B45309' }}>
                                <AlertTriangle size={10} style={{ marginTop: 2, flexShrink: 0 }} /> <span>{w}</span>
                              </div>
                            ))}
                          </td>

                          {/* Icon picker */}
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2 mb-1.5">
                              <IconThumb iconId={surface.icon_id} size={36} alt={surface.icon_name} />
                              {surface.icon_name && (
                                <span className="text-[10px]" style={{ color: 'var(--ph-slate500)' }}>{surface.icon_name}</span>
                              )}
                            </div>
                            <select value={surface.icon_id || ''}
                              onChange={e => updateSurfaceIcon(zIdx, sIdx, e.target.value)}
                              data-testid={`icon-select-${zIdx}-${sIdx}`}
                              className="w-full px-1.5 py-1 border text-[11px]"
                              style={{ borderColor: surface.icon_id ? 'var(--ph-slate200)' : '#FCA5A5', borderRadius: '6px', backgroundColor: surface.icon_id ? '#fff' : '#FEF2F2' }}>
                              <option value="">-- Changer icone --</option>
                              {(refData.icons_available || []).map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                            </select>
                          </td>

                          {/* Products */}
                          <td className="px-3 py-3">
                            {surface.products?.map((prod, pIdx) => {
                              const ok = prod.matched;
                              return (
                                <div key={pIdx} className="mb-1.5">
                                  <div className="flex items-center gap-1.5">
                                    <ConfidenceBadge conf={prod.confidence} matched={prod.matched} />
                                    <select value={prod.product_id || ''}
                                      onChange={e => updateProductId(zIdx, sIdx, pIdx, e.target.value)}
                                      data-testid={`product-select-${zIdx}-${sIdx}-${pIdx}`}
                                      className="flex-1 px-2 py-1.5 border text-xs"
                                      style={{
                                        borderColor: ok ? 'var(--ph-slate200)' : '#FCA5A5',
                                        borderRadius: '8px',
                                        backgroundColor: ok ? '#fff' : '#FEF2F2',
                                        color: ok ? 'var(--ph-ink)' : '#B91C1C',
                                      }}>
                                      <option value="">{prod.product_name ? `! ${prod.product_name} (a choisir)` : '-- Choisir un produit --'}</option>
                                      {refData.products_available.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                    <button onClick={() => removeProduct(zIdx, sIdx, pIdx)} className="p-1 rounded hover:bg-red-50" title="Retirer">
                                      <X size={12} style={{ color: '#DC2626' }} />
                                    </button>
                                  </div>
                                  {(prod.warnings || []).map((w, i) => (
                                    <div key={i} className="mt-0.5 ml-1 text-[10px]" style={{ color: '#B45309' }}>- {w}</div>
                                  ))}
                                </div>
                              );
                            })}
                            <button onClick={() => addProduct(zIdx, sIdx)}
                              className="text-[11px] px-2 py-0.5 rounded flex items-center gap-1 hover:bg-blue-50"
                              style={{ color: 'var(--ph-teal)' }}
                              data-testid={`add-product-${zIdx}-${sIdx}`}>
                              <Plus size={11} /> Produit
                            </button>
                          </td>

                          {/* Method preview */}
                          <td className="px-3 py-3">
                            {(surface.method_steps && surface.method_steps.length > 0) ? (
                              <div className="flex flex-col gap-1.5">
                                {surface.method_steps.map((phase, phIdx) => (
                                  <div key={phIdx}>
                                    <div className="text-[9px] font-bold uppercase tracking-wide mb-0.5" style={{ color: 'var(--ph-navy)' }}>
                                      {phase.phase}
                                    </div>
                                    <div className="flex items-center gap-1 flex-wrap">
                                      {phase.steps.map((stp, sI) => (
                                        <div key={sI} className="flex flex-col items-center" style={{ minWidth: 34 }} title={stp.name}>
                                          <IconThumb iconId={stp.icon_id} size={24} alt={stp.name} />
                                          <span className="text-[8px] text-center leading-tight mt-0.5" style={{ color: 'var(--ph-slate500)' }}>
                                            {stp.name.length > 10 ? stp.name.substring(0, 9) + '.' : stp.name}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-[10px] italic" style={{ color: 'var(--ph-slate400)' }}>
                                (sera genere selon les produits)
                              </span>
                            )}
                          </td>

                          {/* Frequency */}
                          <td className="px-3 py-3">
                            <select value={surface.frequency_id || ''}
                              onChange={e => {
                                const f = refData.frequencies_available.find(fr => fr.id === e.target.value);
                                updateSurfaceField(zIdx, sIdx, 'frequency_id', e.target.value);
                                updateSurfaceField(zIdx, sIdx, 'frequency_name', f?.name || '');
                              }}
                              data-testid={`freq-select-${zIdx}-${sIdx}`}
                              className="w-full px-2 py-1.5 border text-xs" style={{ borderColor: 'var(--ph-slate200)', borderRadius: '8px' }}>
                              <option value="">-- Frequence --</option>
                              {refData.frequencies_available.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                          </td>

                          {/* Control */}
                          <td className="px-3 py-3">
                            <input type="text" value={surface.control || 'Visuel'}
                              onChange={e => updateSurfaceField(zIdx, sIdx, 'control', e.target.value)}
                              className="w-full px-2 py-1.5 border text-xs" style={{ borderColor: 'var(--ph-slate200)', borderRadius: '8px' }} />
                          </td>

                          {/* Responsible */}
                          <td className="px-3 py-3">
                            <input type="text" value={surface.responsible || 'Equipe'}
                              onChange={e => updateSurfaceField(zIdx, sIdx, 'responsible', e.target.value)}
                              className="w-full px-2 py-1.5 border text-xs" style={{ borderColor: 'var(--ph-slate200)', borderRadius: '8px' }} />
                          </td>

                          {/* Remove */}
                          <td className="px-3 py-3 text-center">
                            <button onClick={() => removeSurface(zIdx, sIdx)} className="p-1.5 rounded hover:bg-red-50" data-testid={`remove-surface-${zIdx}-${sIdx}`}>
                              <Trash2 size={14} style={{ color: '#DC2626' }} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="px-5 py-3 border-t" style={{ borderColor: 'var(--ph-slate200)' }}>
                  <button onClick={() => addSurface(zIdx)}
                    className="text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 hover:bg-teal-50"
                    style={{ color: 'var(--ph-teal)', border: '1px dashed var(--ph-teal)' }}
                    data-testid={`add-surface-${zIdx}`}>
                    <Plus size={12} /> Ajouter une surface
                  </button>
                </div>
              </PresthygCard>
            );})}

            <div className="flex items-center justify-between mt-4 mb-6">
              <button onClick={addZone}
                className="text-sm px-4 py-2 rounded-full flex items-center gap-2 hover:bg-teal-50"
                style={{ color: 'var(--ph-teal)', border: '1px dashed var(--ph-teal)' }}
                data-testid="add-zone-btn">
                <Plus size={14} /> Ajouter une zone
              </button>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <PresthygButton variant="secondary" onClick={() => setStep(1)}>Retour</PresthygButton>
              <div className="flex items-center gap-3">
                {pendingZoneCount > 0 && (
                  <span className="text-xs px-3 py-2 rounded-full flex items-center gap-1.5 font-semibold"
                    style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                    data-testid="zones-pending-banner">
                    <AlertTriangle size={12} /> {pendingZoneCount} zone(s) a valider
                  </span>
                )}
                <PresthygButton
                  variant="teal"
                  onClick={handleSave}
                  disabled={saving || pendingZoneCount > 0}
                  data-testid="save-ai-plan-btn">
                  {saving ? 'Sauvegarde...' : <><Save size={16} /> Valider et sauvegarder</>}
                </PresthygButton>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Result */}
        {step === 3 && result && (
          <div data-testid="step-result" className="text-center py-12">
            <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#ECFDF5' }}>
              <CheckCircle size={40} style={{ color: '#059669' }} />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--ph-navy)', fontFamily: 'var(--font-display)' }}>Plan genere et sauvegarde !</h2>
            <p className="text-base mb-1" style={{ color: 'var(--ph-ink)' }}>
              <strong>{result.plan_name}</strong> pour <strong>{result.client_name}</strong>
            </p>
            <p className="text-sm mb-8" style={{ color: 'var(--ph-slate500)' }}>{result.lines_created} ligne(s) creee(s)</p>
            <div className="flex items-center justify-center gap-4">
              <PresthygButton variant="teal" onClick={() => {
                const token = localStorage.getItem('access_token');
                if (token) window.open(`${BACKEND}/api/plans/${result.plan_id}/pdf?mode=preview&token=${encodeURIComponent(token)}`, '_blank');
              }} data-testid="view-ai-pdf">
                <Eye size={16} /> Voir le PDF
              </PresthygButton>
              <PresthygButton variant="secondary" onClick={() => navigate('/plans')}>Voir les plans</PresthygButton>
              <PresthygButton variant="ghost" onClick={() => { setStep(1); setDraftZones([]); setResult(null); setUserText(''); }}>
                <Sparkles size={16} /> Nouveau plan IA
              </PresthygButton>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlanAI;
